import { useApolloClient } from '@apollo/client';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useState } from 'react';

import type {
  ScaledImage,
  ContentTopic,
  EntityPageContractType,
  EntityPageFocusType,
  EntityPagePublicationStatus,
  EntityPageVisibility,
} from '@xing-com/crate-common-graphql-types';
import { Redirect } from '@xing-com/crate-core-router';
import { PageError } from '@xing-com/crate-entity-pages-entity-pages-shared-components-page-error';
import { BREAKPOINTS } from '@xing-com/util';

import { EntityPageDocument } from '../../graphql/queries/entity-page-query.gql-types';
import { useEntityPage } from '../../hooks/use-entity-page/use-entity-page';
import { HeaderModule } from '../../types/header-module';
import { EntityPageSocialProof } from '../../types/social-proof';

export type CoverMediaContextType = {
  cover?: string;
};

export type PageContextType = {
  globalId: string;
  pageSlug: string;
  pageId: string;
  focusType: EntityPageFocusType;
  isCompanyPage: boolean;
  isPremiumPage: boolean;
  isGroupPage: boolean;
  isIndustryPage: boolean;
  isInsiderPage: boolean;
  isPublisherPage: boolean;
  isTopicPage: boolean;
  pageSlogan: string;
  isFollowingPage?: boolean | null;
  companyId?: string | null;
  cppId?: string | null;
  socialProof?: EntityPageSocialProof;
  basePath: string;
  contractType: EntityPageContractType;
  isFreePage: boolean;
  isPaidPage: boolean;
  isEBP: boolean;
  publicationStatus: EntityPagePublicationStatus;
  isPageDraft: boolean;
  isPagePublished: boolean;
  visibility?: EntityPageVisibility | null;
  url?: string;
  pageTitle: string;
  upsellActive?: boolean;
  logo?: string;
  coverImage?: ScaledImage[];
  isShowingCompanyBanner?: boolean;
  isEditor: boolean;
  isAdmin: boolean;
  contentTopicCollection?: ContentTopic[];
  insiderPageName?: string;
  contentPageGlobalId?: string;
  headerModule?: HeaderModule;
  goBackUrl: () => void;
};

export type PageContextValues = {
  pageContext: PageContextType;
  isSubpage?: boolean;
  showCompanyBanner: () => void;
  toggleFollowPage: (follow: boolean) => void;
  setPageLogo: (logo: string) => void;
  setCoverImage: (coverUrl?: string) => void;
};

export const PageContext = createContext<PageContextValues | undefined>(
  undefined
);

type PageContextProviderProps = {
  pageSlug: string;
  isSubpage?: boolean;
};
export const PageContextProvider: FC<
  PropsWithChildren<PageContextProviderProps>
> = ({ pageSlug, isSubpage = false, children }) => {
  const apolloClient = useApolloClient();

  const [isShowingCompanyBanner, setIsShowingCompanyBanner] = useState(false);

  const { pageContext, errorCode, variables, redirectTo } = useEntityPage({
    pageSlug,
  });

  const setPageLogo = (logo: string): void => {
    const response = apolloClient.readQuery({
      query: EntityPageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;

    if (!entityPage || entityPage.__typename !== 'EntityPage') {
      return;
    }

    apolloClient.writeQuery({
      query: EntityPageDocument,
      variables,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          logoImage: [
            {
              __typename: 'ScaledImage',
              url: logo,
              reference: 'medium',
            },
          ],
        },
      },
    });
  };
  const setCoverImage = (coverUrl?: string): void => {
    const response = apolloClient.readQuery({
      query: EntityPageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;

    if (!entityPage || entityPage.__typename !== 'EntityPage') {
      return;
    }

    if (!coverUrl) {
      apolloClient.writeQuery({
        query: EntityPageDocument,
        variables,
        data: {
          ...response,
          entityPageEX: {
            ...entityPage,
            coverImage: [],
          },
        },
      });
      return;
    }

    apolloClient.writeQuery({
      query: EntityPageDocument,
      variables,
      overwrite: true,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          coverImage: Object.values(BREAKPOINTS).map((reference) => ({
            __typename: 'ScaledImage' as const,
            url: coverUrl,
            reference: reference,
          })),
        },
      },
    });
  };
  const toggleFollowPage = (follow: boolean): void => {
    const response = apolloClient.readQuery({
      query: EntityPageDocument,
      variables,
    });

    const entityPage = response?.entityPageEX;
    const headerModule = response?.headerModule;

    if (!entityPage || entityPage.__typename !== 'EntityPage') {
      return;
    }

    const userPageContext = entityPage.userPageContext
      ? {
          ...entityPage.userPageContext,
          userInteractions:
            entityPage.userPageContext?.userInteractions?.__typename ===
            'EntityPageUserInteractionFollow'
              ? {
                  ...entityPage.userPageContext.userInteractions,
                  followState: {
                    ...entityPage.userPageContext.userInteractions.followState,
                    isFollowing: follow,
                  },
                }
              : undefined,
        }
      : undefined;

    const headerContent = (() => {
      switch (headerModule?.__typename) {
        case 'EntityPageHeaderModule': {
          switch (headerModule.content?.__typename) {
            case 'EntityPageCompanyHeaderContent':
            case 'EntityPagePublisherHeaderContent':
            case 'EntityPageTopicPageHeaderContent': {
              if (headerModule.content.followers) {
                return {
                  ...headerModule.content,
                  followers: {
                    ...headerModule.content.followers,
                    total:
                      headerModule.content.followers.total + (follow ? 1 : -1),
                  },
                };
              }
            }
          }
        }
      }

      return undefined;
    })();

    apolloClient.writeQuery({
      query: EntityPageDocument,
      variables,
      data: {
        ...response,
        entityPageEX: {
          ...entityPage,
          userPageContext: userPageContext,
        },
        headerModule: {
          ...headerModule,
          content: headerContent,
        },
      },
    });
  };

  if (redirectTo) return <Redirect to={redirectTo} />;
  if (errorCode || !pageContext) return <PageError type={errorCode} />;

  return (
    <PageContext.Provider
      value={{
        pageContext: {
          ...pageContext,
          isShowingCompanyBanner,
        },
        isSubpage,
        showCompanyBanner: () => setIsShowingCompanyBanner(true),
        toggleFollowPage,
        setPageLogo,
        setCoverImage,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};
